import { useEffect, useCallback, useRef } from 'react';
import { useAuthStore } from '../store/useAuthStore';

const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hour in milliseconds
const ACTIVITY_THROTTLE = 30 * 1000; // Only update activity timestamp every 30 seconds
const STORAGE_KEY = 'bloom-last-activity';

/**
 * Hook to automatically log out users after 1 hour of inactivity.
 *
 * Features:
 * - Tracks mouse, keyboard, touch, and scroll events
 * - Syncs across browser tabs via localStorage
 * - Throttled activity updates for performance
 * - Only active when user is authenticated
 */
export function useInactivityTimeout() {
  const { logout, isAuthenticated } = useAuthStore();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityUpdateRef = useRef<number>(0);

  // Update last activity timestamp (throttled)
  const updateActivity = useCallback(() => {
    const now = Date.now();

    // Throttle updates to avoid excessive localStorage writes
    if (now - lastActivityUpdateRef.current < ACTIVITY_THROTTLE) {
      return;
    }

    lastActivityUpdateRef.current = now;
    localStorage.setItem(STORAGE_KEY, now.toString());
  }, []);

  // Check if session has expired based on last activity
  const checkExpiration = useCallback(() => {
    const lastActivity = localStorage.getItem(STORAGE_KEY);
    if (!lastActivity) {
      updateActivity();
      return false;
    }

    const elapsed = Date.now() - parseInt(lastActivity, 10);
    return elapsed >= INACTIVITY_TIMEOUT;
  }, [updateActivity]);

  // Handle logout due to inactivity
  const handleInactivityLogout = useCallback(async () => {
    console.log('Session expired due to inactivity');
    localStorage.removeItem(STORAGE_KEY);
    await logout();
    // Redirect to login with message
    window.location.href = '/login?reason=inactivity';
  }, [logout]);

  // Reset the inactivity timer
  const resetTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Check if already expired (could happen if tab was in background)
    if (checkExpiration()) {
      handleInactivityLogout();
      return;
    }

    // Calculate time until expiration based on last activity
    const lastActivity = localStorage.getItem(STORAGE_KEY);
    const lastActivityTime = lastActivity ? parseInt(lastActivity, 10) : Date.now();
    const timeUntilExpiration = INACTIVITY_TIMEOUT - (Date.now() - lastActivityTime);

    timeoutRef.current = setTimeout(() => {
      handleInactivityLogout();
    }, Math.max(timeUntilExpiration, 0));
  }, [checkExpiration, handleInactivityLogout]);

  // Handle activity events
  const handleActivity = useCallback(() => {
    updateActivity();
    resetTimer();
  }, [updateActivity, resetTimer]);

  // Listen for activity in other tabs via storage events
  const handleStorageChange = useCallback((event: StorageEvent) => {
    if (event.key === STORAGE_KEY && event.newValue) {
      // Another tab updated activity, reset our timer
      resetTimer();
    }
  }, [resetTimer]);

  useEffect(() => {
    if (!isAuthenticated) {
      // Clear timer and storage when not authenticated
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      localStorage.removeItem(STORAGE_KEY);
      return;
    }

    // Initialize activity timestamp
    updateActivity();
    resetTimer();

    // Activity events to track
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Listen for activity in other tabs
    window.addEventListener('storage', handleStorageChange);

    // Check expiration when tab becomes visible (user returns from another tab/app)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        if (checkExpiration()) {
          handleInactivityLogout();
        } else {
          resetTimer();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, handleActivity, handleStorageChange, updateActivity, resetTimer, checkExpiration, handleInactivityLogout]);
}
